import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { first, isEmpty, isNil } from 'lodash';
import classNames from 'classnames';
// Import Store
import { useAppDispatch } from 'store';
import { changeTemplateValue } from 'store/slices/template';
// Import UI Components
import { Dropdown, ExtendedInput, Icon, TimePicker } from '@geneui/components';
// Import Components
import UploadFile from 'components/common/UploadFile';
import ButtonWithLoader from 'components/common/ButtonWithLoader';
import BlockedBySubscriptionInfo from 'components/common/BlockedBySubscriptionInfo';
// Import Helpers
import { calculateTemplateBodyLengthWithRegEx, convertMinutesToHHMM } from 'services/Helpers';
// Import Services
import {
    TemplateSymbolsCountFields,
    getTemplateValidationTranslatableErrorText,
    ViberTemplateConfig,
} from 'services/template';
import { TemplateHttpService } from 'services/http';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useFieldValidity from 'hooks/UseFieldValidity';
import useImageURLPlaceholder from 'hooks/UseImageURLPlaceholder';
import { useValidator } from 'hooks'; //TODO: change to direct import
// Import Enums
import { TemplateTypeEnum } from 'enums';
// Import Constants
import { BLOCKED_BY_SUBSCRIPTION_CLASSNAME, l, TemplateInputTypes } from 'constants/common';
// Import Styles
import 'assets/scss/socialNotification.scss';

const ViberTemplateContent = ({
    templateData,
    templateLangKey,
    dynamicVariables,
    handleChange,
    shortenerAndTrackerSettings,
    updateDynamicVariableOptions,
    updateInsertionHandler,
}) => {
    const { t } = useTranslation();
    const { doPostRequest } = useRequest();
    const dispatch = useAppDispatch();

    const templateBody = templateData?.[TemplateInputTypes.Body];
    const validityPeriod = convertMinutesToHHMM(
        templateData?.[TemplateInputTypes.ValidityPeriod] ?? ViberTemplateConfig.DefaultValidityPeriodInMinutes,
    );

    const bodyValidationState = useFieldValidity(TemplateInputTypes.Body, l.Body, templateLangKey);
    const buttonTextValidationState = useFieldValidity(TemplateInputTypes.ButtonText, l.ButtonText, templateLangKey);
    const buttonUrlValidationState = useFieldValidity(TemplateInputTypes.ButtonUrl, l.ButtonUrl, templateLangKey);
    const validityPeriodValidationState = useFieldValidity(
        TemplateInputTypes.ValidityPeriod,
        l.ValidityPeriod,
        templateLangKey,
    );
    const imageURLPlaceholderDropdownValidationState = useFieldValidity(
        TemplateInputTypes.ImageURLPlaceholderDropdown,
        l.ImageURLPlaceholderDropdown,
        templateLangKey,
    );

    const validator = useValidator(getTemplateValidationTranslatableErrorText(t));

    const [isUploadedImageLoading, setIsUploadedImageLoading] = useState(false);
    const [isUploadBtnDisabled, setIsUploadBtnDisabled] = useState(true);
    const [file, setFile] = useState<Nullable<Blob>>(null);
    const [bodyValue, setBodyValue] = useState(templateBody);
    const [moveCursorToPosition, setMoveCursorToPosition] = useState<number | null>(null);

    const inputRef = useRef<HTMLInputElement>(null);

    const computedBody = useMemo(() => {
        if (isEmpty(shortenerAndTrackerSettings) || isEmpty(templateBody)) return '';
        return calculateTemplateBodyLengthWithRegEx(shortenerAndTrackerSettings, templateBody);
    }, [templateBody, shortenerAndTrackerSettings]);

    // kept out of the hook: an asset upload must not be aborted when the user switches tabs or leaves the page
    const { addAssetRequest } = useMemo(
        () => ({
            // eslint-disable-next-line no-restricted-syntax -- upload must survive unmount
            addAssetRequest: TemplateHttpService.addAsset(),
        }),
        [],
    );

    const {
        handleImageURLPlaceholderDropdownChange,
        imagePreviewURL,
        imageURLPlaceholderDropdownData,
        isBlockedImageURLPlaceholderDropdown,
        isDisabledUploadFileBtn,
        isDisabledImageURLPlaceholderDropdown,
    } = useImageURLPlaceholder({
        templateData,
        templateLangKey,
        templateType: TemplateTypeEnum.Viber,
        dynamicVariables,
    });

    const fileInputChangeHandler = (e) => {
        const fileInput = e.currentTarget;
        setFile(first(fileInput.files));
        dispatch(
            changeTemplateValue({
                propertyName: TemplateInputTypes.FileName,
                value: !fileInput.value ? null : first<AnyObject>(fileInput.files)?.name,
                templateLangKey,
            }),
        );
        dispatch(
            changeTemplateValue({
                propertyName: TemplateInputTypes.UploadedImageUrl,
                value: '',
                templateLangKey,
            }),
        );
        setIsUploadBtnDisabled(!fileInput.value);
    };

    const uploadImgHandler = () => {
        if (isNil(file)) return;
        setIsUploadedImageLoading(true);
        const formData = new FormData();
        formData.append('file', file);

        doPostRequest(addAssetRequest.request, {
            requestBody: formData,
            successCallback: (Data) => {
                dispatch(
                    changeTemplateValue({
                        propertyName: TemplateInputTypes.UploadedImageUrl,
                        value: first(Data)?.Url,
                        templateLangKey,
                    }),
                );
                setIsUploadedImageLoading(false);
                setIsUploadBtnDisabled(true);
            },
            errorCallback: () => {
                setIsUploadedImageLoading(false);
                setIsUploadBtnDisabled(false);
            },
        });
    };

    const buttonTextInputChangeHandler = (e) => {
        const value = e.target.value;
        dispatch(changeTemplateValue({ propertyName: TemplateInputTypes.ButtonText, value: value, templateLangKey }));
        validator({ [templateLangKey + 'ButtonUrl']: { text: value, url: templateData?.ButtonUrl } });
        validator({ [templateLangKey + 'ButtonText']: { text: value, url: templateData?.ButtonUrl } });
    };

    const buttonUrlInputChangeHandler = (e) => {
        const value = e.target.value;
        dispatch(changeTemplateValue({ propertyName: TemplateInputTypes.ButtonUrl, value: value, templateLangKey }));
        validator({ [templateLangKey + 'ButtonUrl']: { text: templateData?.ButtonText, url: value } });
        validator({ [templateLangKey + 'ButtonText']: { text: templateData?.ButtonText, url: value } });
    };

    const deleteUploadedImageHandler = () => {
        dispatch(
            changeTemplateValue({
                propertyName: TemplateInputTypes.UploadedImageUrl,
                value: '',
                templateLangKey,
            }),
        );
        dispatch(
            changeTemplateValue({
                propertyName: TemplateInputTypes.FileName,
                value: '',
                templateLangKey,
            }),
        );
        setIsUploadBtnDisabled(true);
        setFile(null);
    };

    const onChange = (e) => {
        const value = e.target.value;

        setBodyValue(value);
        handleChange(TemplateInputTypes.Body, value);
    };

    const onBodyFocus = useCallback(() => {
        updateDynamicVariableOptions({ hintText: '', focusedInputKey: TemplateInputTypes.Body });
    }, [updateDynamicVariableOptions]);

    const onButtonTextFocus = () => {
        updateDynamicVariableOptions({
            hintText: l.DynamicVariablesAreNotSupported,
            focusedInputKey: TemplateInputTypes.ButtonText,
        });
    };

    const onButtonUrlFocus = () => {
        updateDynamicVariableOptions({
            hintText: l.DynamicVariablesAreNotSupported,
            focusedInputKey: TemplateInputTypes.ButtonUrl,
        });
    };

    const insertTextAtCursor = (dynamicVariable) => {
        const textToInsert = dynamicVariable.FieldName;
        const selectionStart = inputRef.current?.selectionStart;
        const firstPart = bodyValue.slice(0, selectionStart);
        const secondPart = bodyValue.slice(selectionStart, bodyValue.length);
        const insertedText = ` {${textToInsert}} `;
        const resultValue = `${firstPart}${insertedText}${secondPart}`;

        setBodyValue(resultValue);
        setMoveCursorToPosition(firstPart.length + insertedText.length);
        handleChange(TemplateInputTypes.Body, resultValue);
    };

    updateInsertionHandler(TemplateInputTypes.Body, insertTextAtCursor);

    // The caret is placed after the textarea receives the new value; setting it earlier is lost
    // because writing the controlled value moves the caret to the end.
    useEffect(() => {
        if (!isNil(moveCursorToPosition)) {
            inputRef.current?.focus();
            inputRef.current?.setSelectionRange(moveCursorToPosition, moveCursorToPosition);
            setMoveCursorToPosition(null);
        }
    }, [moveCursorToPosition]);

    const timePickerChangeHandler = (e) => {
        const value = e.target.value;
        const minutes = moment.duration(value).asMinutes();

        handleChange(TemplateInputTypes.ValidityPeriod, String(minutes));
    };

    return (
        <>
            <div className="crm-create-template-page-c-c-content">
                <div className="crm-messaging-row">
                    <div className="crm-messaging-row-head">
                        <p>{t(l.SymbolsCountIn, { pageName: TemplateTypeEnum.Viber })}</p>
                        <span>{`(${computedBody.length} ${t(l.Bytes)} / ${
                            TemplateSymbolsCountFields[TemplateTypeEnum.Viber]
                        } ${t(l.Bytes)})`}</span>
                    </div>
                    <ExtendedInput
                        ref={inputRef}
                        type="textarea"
                        value={bodyValue}
                        colorBorderOnError={true}
                        isValid={bodyValidationState.isValid}
                        errorText={bodyValidationState.errorText}
                        placeholder={t(l.TypeYourText)}
                        onChange={onChange}
                        onFocus={onBodyFocus}
                        // eslint-disable-next-line jsx-a11y/no-autofocus
                        autoFocus={true}
                    />
                </div>
            </div>
            <div className="viber-template-validity-period-container">
                <div className="viber-template-validity-period-section">
                    <p className="viber-template-validity-period-title">{t(l.ValidityPeriod)}:</p>
                    <div className="viber-template-validity-period-input-container">
                        <TimePicker
                            appearance="outline"
                            showSeconds={false}
                            onChange={timePickerChangeHandler}
                            value={validityPeriod}
                            isValid={validityPeriodValidationState.isValid}
                            errorText={validityPeriodValidationState.errorText}
                        />
                    </div>
                </div>
            </div>
            <div className="social-template-section-container">
                <div className="social-n-details-panel">
                    <div className="social-n-details-panel-content">
                        <div>
                            <BlockedBySubscriptionInfo
                                isBlocked={isBlockedImageURLPlaceholderDropdown}
                                position="right"
                            >
                                <div
                                    className={classNames('crm-image-url-placeholder-dropdown', {
                                        [`${BLOCKED_BY_SUBSCRIPTION_CLASSNAME} not-clickable`]:
                                            isBlockedImageURLPlaceholderDropdown,
                                    })}
                                >
                                    <Dropdown
                                        clearable={true}
                                        data={imageURLPlaceholderDropdownData}
                                        value={templateData?.UploadedImageUrl}
                                        onChange={handleImageURLPlaceholderDropdownChange}
                                        disabled={isDisabledImageURLPlaceholderDropdown}
                                        placeholder={t(l.ImageURLPlaceholderDropdown)}
                                        isValid={imageURLPlaceholderDropdownValidationState.isValid}
                                        errorText={imageURLPlaceholderDropdownValidationState.errorText}
                                    />
                                </div>
                            </BlockedBySubscriptionInfo>
                            <div className="social-n-details-panel-choose-file">
                                <p className="social-n-details-panel-choose-file-title"> {t(l.Media)}:</p>
                                <div className="social-n-details-panel-browse">
                                    <UploadFile
                                        value={templateData?.FileName}
                                        onChange={fileInputChangeHandler}
                                        acceptedExtensions=".svg,.png,.jpg"
                                        disabled={isDisabledUploadFileBtn}
                                    />
                                </div>
                                <div>
                                    <ButtonWithLoader
                                        onClick={uploadImgHandler}
                                        disabled={
                                            isUploadBtnDisabled || isDisabledUploadFileBtn || isUploadedImageLoading
                                        }
                                        isLoading={isUploadedImageLoading}
                                    >
                                        {t(l.Upload)}
                                    </ButtonWithLoader>
                                </div>

                                <div>
                                    <div className="social-c-file-upload-details">
                                        <p>{t(l.MediaPreview)}:</p>
                                        <div className="social-c-file-upload-details-img-wrapper">
                                            {imagePreviewURL && <img alt="noImage" src={imagePreviewURL} />}
                                        </div>
                                        <Icon
                                            type="bc-icon-trash"
                                            disabled={!imagePreviewURL}
                                            onClick={deleteUploadedImageHandler}
                                        />
                                    </div>
                                </div>
                            </div>
                            <p>{t(l.ImageUploadRequirements)}</p>
                        </div>
                    </div>
                </div>
                <div className="social-inputs-container">
                    <ExtendedInput
                        appearance="outline"
                        inputSize="default"
                        labelAppearance="swap"
                        placeholder={t(l.ButtonText)}
                        onChange={buttonTextInputChangeHandler}
                        value={templateData?.ButtonText}
                        onFocus={onButtonTextFocus}
                        isValid={buttonTextValidationState.isValid}
                        errorText={buttonTextValidationState.errorText}
                    />
                    <ExtendedInput
                        appearance="outline"
                        inputSize="default"
                        labelAppearance="swap"
                        placeholder={t(l.ButtonUrl)}
                        onChange={buttonUrlInputChangeHandler}
                        value={templateData?.ButtonUrl}
                        onFocus={onButtonUrlFocus}
                        isValid={buttonUrlValidationState.isValid}
                        errorText={buttonUrlValidationState.errorText}
                    />
                </div>
            </div>
        </>
    );
};

ViberTemplateContent.propTypes = {
    templateData: PropTypes.object.isRequired,
    templateLangKey: PropTypes.string.isRequired,
    handleChange: PropTypes.func.isRequired,
    shortenerAndTrackerSettings: PropTypes.array.isRequired,
    updateDynamicVariableOptions: PropTypes.func.isRequired,
    updateInsertionHandler: PropTypes.func.isRequired,
};

ViberTemplateContent.defaultProps = {
    focusOnMount: true,
};

export default ViberTemplateContent;
